import React, {  useEffect } from 'react';
import { useParams, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components/macro';

import { Allow } from '../../auth';
import { AppBarIconLink, AppBarPartnerLink, AppBarDinteroLogo, AppBarIconExternalLink } from '.';
import { distances, palette } from '../../styles/constants';

interface MobileNavMenuProps {
    onClose: () => void;
}

const MobileNavMenu = (
    {
        onClose,
    }: MobileNavMenuProps
) => {
    const location = useLocation();
    useEffect(() => {
        return () => onClose();
    }, [location, onClose]);

    const { accountId } = useParams();
    const { t } = useTranslation();

    return (
        <Container>
            <CloseButton onClick={() => onClose()} />
            <MenuItemsWrapper>
                <div>
                    <AppBarIconLink to={`/${accountId}`} icon="backoffice" title={t('app_bar.titles.dashboard')} exact />
                    <Allow anyOfScopes={[['admin'], ['admin:orders'], ['read:orders'], ['admin:shopping'], ['read:shopping']]}>
                        <AppBarIconLink to={`/${accountId}/orders`} icon="orders" title={t('app_bar.titles.orders')} />
                    </Allow>
                    <Allow anyOfScopes={[['admin'], ['admin:checkout'], ['read:checkout']]}>
                        <AppBarIconLink to={`/${accountId}/payments`} icon="payments" title={t('app_bar.titles.payments')} />
                    </Allow>
                    <Allow anyOfScopes={[['admin'], ['admin:customers'], ['read:customers']]}>
                        <AppBarIconLink to={`/${accountId}/customers`} icon="customers" title={t('app_bar.titles.customers')} />
                    </Allow>
                    <Allow anyOfScopes={[['admin'], ['admin:discounts'], ['read:discounts']]}>
                        <AppBarIconLink
                            to={`/${accountId}/marketing/discounts`}
                            icon="campaigns"
                            title={t('app_bar.titles.marketing')}
                        />
                    </Allow>
                    <Allow
                        anyOfScopes={[['read:payout'], ['admin:payout'], ['read:payout:/admin'], ['admin:payout:/admin']]}
                        scope="admin:dintero"
                    >
                        <AppBarIconLink
                            to={`/${accountId}/payout`}
                            icon="payments" // TODO: Icon
                            title={t('app_bar.titles.payout', 'Payout')}
                        />
                    </Allow>
                    <Allow anyOfScopes={[['admin'], ['admin:accounts']]}>
                        <AppBarIconLink to={`/${accountId}/settings`} icon="settings" title={t('app_bar.titles.settings')} />
                    </Allow>
                    <AppBarPartnerLink accountId={accountId ?? ''} />
                </div>
                <div>
                    <AppBarIconExternalLink
                        icon="docs"
                        title={t('app_bar.titles.docs')}
                        href="https://docs.dintero.com"
                    />

                    <AppBarIconExternalLink
                        icon="help"
                        title={t('app_bar.titles.help')}
                        href="https://www.dintero.com/our-services/backoffice"
                    />
                    <AppBarDinteroLogo />
                </div>
            </MenuItemsWrapper>

        </Container>
    );
};

const Container = styled.div`
    display: none;

    @media (max-width: 768px) {
        display: block;
        position: fixed;
        width: 100%;
        height: 100%;
        top: 0;
        background-color: white;
        padding: 50px 0 0;
        overflow: hidden;
        z-index: 100;
    } ;
`;

const MenuItemsWrapper = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
    padding: ${distances.small} 0;
`;

const CloseButton = styled.button`
    position: absolute;
    right: calc(${distances.small} + 8px);
    top: ${distances.small};
    width: 30px;
    height: 30px;
    border: none;
    outline: none;
    background-color: transparent;
    cursor: pointer;

    &:before,
    &:after {
        position: absolute;
        top: 0px;
        content: ' ';
        height: 30px;
        width: 2px;
        background-color: ${palette.neutral[600]};
    }

    &:before {
        transform: rotate(45deg);
    }

    &:after {
        transform: rotate(-45deg);
    }
`;

export default MobileNavMenu;
